<template>
  <div class="wt2-dashboard-story-versions wt2-breakpoints">
    <div class="-panel">
      <div class="-heading">
        <wt-button
          class="-back"
          type="hollow-dark"
          icon="wticon-chevron-left"
          :text="backLabel"
          @click="backToDrafts"
        />
        <div class="-info">
          <div
            class="wt2-text-xl -title"
            :title="pageTitle"
            v-text="pageTitle"
          />
          <div v-if="group" class="-subtitle wt2-text-s" v-text="subtitle" />
        </div>
      </div>
      <div v-if="group" class="-control">
        <wt-dropdown
          type="normal right"
          :items="sortItems"
          :on-click-item="setSort"
          :current-item-id="sortOrder"
          class="wt2-programmers-dropdown align-self-center"
        />
      </div>
    </div>

    <div v-if="group" class="-content">
      <ul class="wt2-story-previews list-inline">
        <story-version-card
          v-for="version in sortedVersions"
          :key="version.storyId"
          :version="version"
          @open="openVersion"
          @branch="branchVersion"
          @delete="deleteVersion"
        />
      </ul>
    </div>

    <div class="wt-dashboard-loader" v-show="isFetching">
      <div class="wt-loader"></div>
    </div>
  </div>
</template>

<script>
import app from 'app';
import Analytics from 'c/WtAnalytics';
import { mapGetters } from 'vuex';
import StoryVersionCard from 'v/Dashboard/StoryVersionCard.vue';

export default {
  name: 'dashboard-story-versions',

  components: {
    StoryVersionCard,
  },

  data() {
    return {
      // Any group member id, taken from the route dashboard/versions/{id}
      storyId: null,
      // The stable group key, captured once the group resolves. The page is
      // anchored on this (not storyId) so deleting the anchor version doesn't
      // blank the page - the deleted id drops out of storyToFolder.
      folderId: null,
      sortOrder: 'newest',
      isFetching: false,
    };
  },

  created() {
    this.storyId = this.$_storyIdFromRoute();
    this.load();
  },

  computed: {
    ...mapGetters('storyVersions', ['groupByStoryId', 'groupByFolderId']),

    group() {
      // Prefer the stable folderId once resolved; fall back to the route id
      // until the first fetch lands.
      if (this.folderId) {
        return this.groupByFolderId(this.folderId);
      }
      return this.storyId ? this.groupByStoryId(this.storyId) : null;
    },

    activeVersion() {
      if (!this.group) {
        return null;
      }
      return (
        this.group.versions.find(
          (v) => v.storyId === this.group.activeStoryId
        ) ||
        this.group.versions.find((v) => v.root) ||
        this.group.versions[0]
      );
    },

    pageTitle() {
      return this.activeVersion
        ? this.activeVersion.title || 'Untitled'
        : app.culture.storyVersions.versionHistory;
    },

    backLabel() {
      return app.culture.storyVersions.backToDrafts;
    },

    sortedVersions() {
      if (!this.group) {
        return [];
      }
      // The API returns root-first, ascending versionNumber. "Newest first"
      // reverses that (highest number first, original last).
      const versions = this.group.versions.slice();
      return this.sortOrder === 'newest' ? versions.reverse() : versions;
    },

    subtitle() {
      if (!this.group) {
        return '';
      }
      const count = this.group.versions.length;
      const parts = [`${count} ${app.culture.storyVersions.versionsCount}`];
      const latestTs = app.moment(this.group.lastModifiedDate).valueOf();
      parts.push(
        `${app.culture.storyVersions.latestPrefix} ${app.TIME.getTimeAgo(
          this.$store.state.currentTime - latestTs
        )}`
      );
      return parts.join(' · ');
    },

    sortItems() {
      return [
        { id: 'newest', label: app.culture.storyVersions.newestFirst },
        { id: 'oldest', label: app.culture.storyVersions.oldestFirst },
      ];
    },
  },

  methods: {
    $_storyIdFromRoute() {
      const path = this.$store.getters.routePath || '';
      const match = path.match(/dashboard\/versions\/([^/?]+)/);
      return match ? match[1] : null;
    },

    load() {
      if (!this.storyId) {
        this.backToDrafts();
        return;
      }
      this.isFetching = true;
      this.$store
        .dispatch('storyVersions/FETCH_VERSIONS', {
          storyId: this.storyId,
          force: true,
        })
        .then(
          (group) => {
            this.isFetching = false;
            // Anchor the page on the stable folder key from here on.
            if (group) {
              this.folderId = group.folderId;
            }
            Analytics.trigger('analytics:versions:pageOpened', {
              context: 'dashboard',
              versionsCount: this.group ? this.group.versions.length : 0,
            });
          },
          () => {
            this.isFetching = false;
            // Not a group (or gone) - nothing to show here.
            this.backToDrafts();
          }
        );
    },

    setSort(id) {
      this.sortOrder = id;
    },

    backToDrafts() {
      app.navigate('dashboard/inProgress');
    },

    openVersion(storyId) {
      Analytics.trigger('analytics:versions:openVersion', {
        context: 'dashboard',
      });
      app.navigate(`stories/${storyId}`);
    },

    branchVersion(storyId) {
      const folderId = this.group ? this.group.folderId : null;

      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_2,
          title: app.culture.storyVersions.branchConfirm,
          buttonYesText: app.culture.storyVersions.branchConfirmYes,
          buttonNoText: app.culture.generic.CANCEL,
        })
        .then((data) => {
          if (!data.action) {
            return;
          }
          app.delayedSpinner.show();
          this.$store
            .dispatch('storyVersions/BRANCH_VERSION', { storyId, folderId })
            .then(
              (res) => {
                Analytics.trigger('analytics:versions:branch', {
                  context: 'dashboard',
                });
                this.$_syncLineageAfterBranch(res);
                app.delayedSpinner.hide();
              },
              (err) => {
                app.delayedSpinner.hide();
                if (err && err.errorCode === 400) {
                  app.dialog.error({
                    type: app.dialog.types.ERROR_1,
                    title: app.culture.storyVersions.branchRootError,
                  });
                }
              }
            );
        });
    },

    $_syncLineageAfterBranch(res) {
      if (!res) {
        return;
      }
      const updates = [
        {
          id: res.rootStoryId,
          folderId: res.newFolderId || null,
          parentId: null,
          versionNumber: null,
        },
      ];
      this.$store.commit('syncStoryLineage', updates);
    },

    deleteVersion(storyId) {
      const folderId = this.group ? this.group.folderId : null;

      Analytics.trigger('analytics:versions:delete', { context: 'dashboard' });

      // Drop the card right away so the grid reflows immediately (matching the
      // drafts list) instead of leaving an empty slot until the round-trips
      // finish. The server reshape is reconciled below; a failed delete is
      // rolled back by the force refetch in .fail.
      this.$store.commit('storyVersions/removeVersionFromGroup', {
        folderId,
        storyId,
      });

      app.api.draftStories
        .delete({ id: storyId })
        .done(() => {
          this.$store.commit('deleteStory', storyId);
          this.$store
            .dispatch('storyVersions/REFRESH_AFTER_DELETE', {
              deletedStoryId: storyId,
              folderId,
            })
            .then((refreshed) => {
              if (!refreshed || refreshed.versions.length <= 1) {
                // Group dissolved into a standalone draft.
                this.backToDrafts();
              }
            });
        })
        .fail((err) => {
          if (err && err.errorCode === 423) {
            app.dialog.error({
              type: app.dialog.types.ERROR_1,
              title: app.culture.storyVersions.deleteLocked,
            });
          } else {
            console.error(`failed deleting version ${storyId}`);
          }
          // Reconcile via a SURVIVING member, not the just-deleted id: a 423
          // restores the full group (nothing was deleted), while a concurrent
          // 404 reshapes to the survivors instead of anchoring the refetch on
          // the deleted id (which would 404 again and wipe the whole group).
          this.$store
            .dispatch('storyVersions/REFRESH_AFTER_DELETE', {
              deletedStoryId: storyId,
              folderId,
            })
            .then((refreshed) => {
              if (!refreshed || refreshed.versions.length <= 1) {
                this.backToDrafts();
              }
            });
        });
    },
  },
};
</script>
